/**
 * Common constants and enums used across the platform
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * 3-level quality indicator for matches, confidence, and similar use cases.
 * Use this for simple high/medium/low quality scales.
 *
 * For 4-level severity scales (with critical), use RecommendationSeverity.
 * For 5-level priority scales (with urgent/critical), use CasePriority.
 */
export enum MatchLevel {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

/**
 * 4-level confidence scale for AI response confidence and similar assessments.
 *
 * Matches ConfidenceAssessment['level'] from chat-chunk.contract.ts.
 */
export enum ConfidenceLevel {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  VERY_HIGH = 'very_high',
}

/**
 * Execution lifecycle status for tool invocations, background tasks, and similar workflows.
 *
 * Use this for any status that follows the pending -> executing -> completed/failed pattern.
 */
export enum ToolExecutionStatus {
  PENDING = 'pending',
  EXECUTING = 'executing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

/**
 * Health status indicators
 */
export enum HealthStatus {
  UP = 'up',
  DOWN = 'down',
  UNKNOWN = 'unknown',
}

/**
 * Job status in the queue
 */
export enum JobStatus {
  WAITING = 'waiting',
  ACTIVE = 'active',
  COMPLETED = 'completed',
  FAILED = 'failed',
  DELAYED = 'delayed',
}

/**
 * OAuth provider types
 */
export enum OAuthProvider {
  SLACK = 'slack',
  SALESFORCE = 'salesforce',
  GOOGLE = 'google',
  GITHUB = 'github',
}

/**
 * Webhook event types (integration-specific)
 */
export enum WebhookEventType {
  SLACK_MESSAGE = 'slack.message',
  SLACK_REACTION = 'slack.reaction',
  SALESFORCE_ACCOUNT_CREATED = 'salesforce.account.created',
  SALESFORCE_ACCOUNT_UPDATED = 'salesforce.account.updated',
  SALESFORCE_OPPORTUNITY_CREATED = 'salesforce.opportunity.created',
}

/**
 * Model provider types
 */
export enum ModelProvider {
  OPENAI = 'openai',
  ANTHROPIC = 'anthropic',
  GOOGLE = 'google',
  COHERE = 'cohere',
  META = 'meta',
  BEDROCK = 'bedrock',
  VERTEX = 'vertex',
}

/**
 * AI Model types
 */
export enum ModelType {
  CHAT = 'chat',
  COMPLETION = 'completion',
  EMBEDDING = 'embedding',
  IMAGE = 'image',
}

/**
 * AI Model status
 */
export enum ModelStatus {
  ACTIVE = 'active',
  DISABLED = 'disabled',
  DEPRECATED = 'deprecated',
}

/**
 * Provider health status
 */
export enum ProviderHealthStatus {
  HEALTHY = 'healthy',
  DEGRADED = 'degraded',
  DOWN = 'down',
}

/**
 * User theme preference
 */
export enum ThemePreference {
  LIGHT = 'light',
  DARK = 'dark',
  SYSTEM = 'system',
}

/**
 * Cache key prefixes for organized caching
 */
export enum CacheKeyPrefix {
  USER = 'user',
  SESSION = 'session',
  API_KEY = 'api-key',
  OAUTH_TOKEN = 'oauth-token',
  RATE_LIMIT = 'rate-limit',
  CIRCUIT_BREAKER = 'circuit-breaker',
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Pagination defaults
 */
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 100,
} as const;

/**
 * Rate limiting defaults
 */
export const RATE_LIMITS = {
  FREE_REQUESTS_PER_MINUTE: 10,
  STARTER_REQUESTS_PER_MINUTE: 60,
  PROFESSIONAL_REQUESTS_PER_MINUTE: 300,
  ENTERPRISE_REQUESTS_PER_MINUTE: 1000,
} as const;

/**
 * Token limits per tier
 */
export const TOKEN_LIMITS = {
  FREE_MONTHLY_TOKENS: 10000,
  STARTER_MONTHLY_TOKENS: 100000,
  PROFESSIONAL_MONTHLY_TOKENS: 1000000,
  ENTERPRISE_MONTHLY_TOKENS: -1, // Unlimited
} as const;

/**
 * Document processing limits
 */
export const DOCUMENT_LIMITS = {
  MAX_FILE_SIZE_MB: 50,
  MAX_FILES_PER_UPLOAD: 10,
  SUPPORTED_FORMATS: ['pdf', 'docx', 'txt', 'md', 'csv'] as const,
} as const;

/**
 * Message limits
 */
export const MESSAGE_LIMITS = {
  MAX_MESSAGE_LENGTH: 32000,
  MAX_CONTEXT_MESSAGES: 50,
} as const;

/**
 * Agent configuration defaults
 */
export const AGENT_DEFAULTS = {
  DEFAULT_TEMPERATURE: 0.7,
  DEFAULT_MAX_TOKENS: 2000,
  DEFAULT_TOP_P: 0.9,
} as const;

/**
 * Embedding configuration
 */
export const EMBEDDING_CONFIG = {
  DIMENSION: 1536, // OpenAI ada-002
  CHUNK_SIZE: 512,
  CHUNK_OVERLAP: 50,
} as const;

/**
 * Cache TTL values (in seconds)
 */
export const CACHE_TTL = {
  SHORT: 300, // 5 minutes
  MEDIUM: 1800, // 30 minutes
  LONG: 3600, // 1 hour
  VERY_LONG: 86400, // 24 hours
} as const;

/**
 * API versioning
 */
export const API_VERSION = 'v1' as const;

/**
 * Error codes
 */
export const ERROR_CODES = {
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
} as const;
